import { css } from "@linaria/core"
import { AnimatePresence, motion } from "framer-motion"
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react"

export type HeaderCell = {
  field?: string
  sortable?: boolean
  sx?: any
  label: ReactNode
  align?: "left" | "center" | "right" | "justify" | "char"
}

type InMemoryTableProps = {
  headerCells: HeaderCell[]
  data: any[]
  /**
   * @default 30
   */
  pageSize?: number
  renderRow: (row: any, index: number) => React.ReactNode
  initialSortField: string
  initialSortDir: "asc" | "desc"
  loading?: boolean
}

export function InMemoryTable(props: InMemoryTableProps) {
  const {
    data,
    pageSize = 30,
    renderRow,
    headerCells,
    initialSortField,
    initialSortDir,
    loading,
    ...rest
  } = props

  const loaderRef = useRef(null)

  const [listSize, setListSize] = useState(pageSize)
  const [endReached, setEndReached] = useState(false)

  useEffect(() => {
    setListSize(pageSize)
    setEndReached(false)
  }, [data, pageSize])

  useEffect(() => {
    if (endReached) return
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first.isIntersecting) {
          console.log("Intersecting - Showing more data")
          setListSize((prev) => {
            if (prev + pageSize >= data.length) {
              setEndReached(true)
              return data.length
            }
            return prev + pageSize
          })
        } else {
          console.log("Not intersecting")
        }
      },
      { threshold: 1 },
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => observer.disconnect()
  }, [data.length, endReached, pageSize])

  const [sortAscending, setSortAscending] = useState<boolean>(initialSortDir === "asc")
  const [sortField, setSortField] = useState<string>(initialSortField)

  const visibleRows = useMemo(
    () =>
      [...data]
        .sort((a, b) => {
          if (a[sortField] < b[sortField]) {
            return sortAscending ? -1 : 1
          }
          if (a[sortField] > b[sortField]) {
            return sortAscending ? 1 : -1
          }
          return 0
        })
        .slice(0, listSize),
    [data, listSize, sortAscending, sortField],
  )

  return (
    <>
      <table
        className={css`
          border-collapse: collapse;
        `}
      >
        <thead>
          <tr
            className={css`
              overflow: hidden;
              & > th {
                border-top: 1px solid var(--mui-palette-titleBarBorder);
              }
              & > th:first-child {
                border-left: 1px solid var(--mui-palette-titleBarBorder);
                border-top-left-radius: var(--mui-shape-borderRadius);
              }
              & > th:last-child {
                border-right: 1px solid var(--mui-palette-titleBarBorder);
                border-top-right-radius: var(--mui-shape-borderRadius);
              }
            `}
          >
            {headerCells.map((cell, index) => (
              <th
                key={index}
                align={cell.align || "left"}
                className={css`
                  font-weight: 400;
                  padding: calc(var(--spacing) * 2);
                  border: 0px;
                  color: var(--mui-palette-text-secondary);
                  font-size: 0.875rem;
                  background: var(--mui-palette-titleBar);
                  border-bottom: 1px solid var(--mui-palette-titleBarBorder);
                `}
              >
                {cell.label}
              </th>
            ))}
          </tr>
        </thead>
        <AnimatePresence initial={false}>
          <motion.tbody>{visibleRows.map(renderRow)}</motion.tbody>
        </AnimatePresence>
      </table>
    </>
  )
}
